/**
 * Estado de la interfaz: tema claro/oscuro y avisos (toasts).
 */

import { create } from "zustand";

import { conMayuscula } from "@/lib/texto";

// =============================================================================
//  TEMA CLARO / OSCURO
// =============================================================================

const LLAVE_TEMA = "softfi-tema";

export type Tema = "light" | "dark";

type EstadoTema = {
  tema: Tema;
  alternar: () => void;
  poner: (tema: Tema) => void;
};

function temaInicial(): Tema {
  try {
    const guardado = localStorage.getItem(LLAVE_TEMA);
    if (guardado === "light" || guardado === "dark") return guardado;
  } catch {
    /* nada */
  }
  /*
    ⚠️ SIN ELECCIÓN, CLARO. Antes seguía la preferencia del sistema operativo.

    Un teléfono Android barato con el ahorro de batería puesto llega en oscuro,
    y el bodeguero que abre Softfi por primera vez ve una pantalla negra que no
    eligió y que no se parece a lo que le enseñaron.

    ⚠️ Esta regla está DUPLICADA en `index.html`, en el script que corre antes
    de React para evitar el flashazo. Tienen que decir lo mismo: si una dijera
    «claro» y la otra «según el sistema», la página parpadearía de un tema al
    otro al cargar. Cambiar una obliga a cambiar la otra.
  */
  return "light";
}

function aplicarTema(tema: Tema) {
  document.documentElement.classList.toggle("dark", tema === "dark");
  try {
    localStorage.setItem(LLAVE_TEMA, tema);
  } catch {
    /* nada */
  }
}

export const useTema = create<EstadoTema>((set, get) => ({
  tema: temaInicial(),

  alternar: () => {
    const nuevo: Tema = get().tema === "dark" ? "light" : "dark";
    aplicarTema(nuevo);
    set({ tema: nuevo });
  },

  poner: (tema) => {
    aplicarTema(tema);
    set({ tema });
  },
}));

// =============================================================================
//  AVISOS (TOASTS)
// =============================================================================
//
// Confirmaciones y errores que aparecen arriba y se van solos. En un POS son
// preferibles a los `alert()` del navegador: no bloquean la pantalla y el cajero
// puede seguir trabajando.

export type TipoAviso = "exito" | "error" | "info";

export type Aviso = {
  id: number;
  tipo: TipoAviso;
  mensaje: string;
  /** Texto secundario opcional, para explicar qué hacer. */
  detalle?: string;
};

type EstadoAvisos = {
  avisos: Aviso[];
  mostrar: (tipo: TipoAviso, mensaje: string, detalle?: string) => void;
  quitar: (id: number) => void;
};

let contador = 0;

export const useAvisos = create<EstadoAvisos>((set, get) => ({
  avisos: [],

  mostrar: (tipo, mensaje, detalle) => {
    const id = ++contador;
    // ⚠️ La mayúscula se pone AQUÍ, no en cada sitio que avisa.
    //
    // Los avisos vienen de tres lados: escritos en el código, traídos del
    // servidor y escritos por el propio dueño. Repasar el primero a mano
    // arregla hoy, no arregla el que se escriba mañana, y no toca los otros
    // dos — que son justo los que nadie puede repasar.
    set({
      avisos: [
        ...get().avisos,
        { id, tipo, mensaje: conMayuscula(mensaje), detalle: detalle && conMayuscula(detalle) },
      ],
    });

    // Los errores duran más: hay que alcanzar a leerlos.
    const duracion = tipo === "error" ? 6000 : 3000;
    setTimeout(() => get().quitar(id), duracion);
  },

  quitar: (id) => set({ avisos: get().avisos.filter((a) => a.id !== id) }),
}));

/** Atajos para no escribir el tipo cada vez. */
export const aviso = {
  exito: (mensaje: string, detalle?: string) =>
    useAvisos.getState().mostrar("exito", mensaje, detalle),
  error: (mensaje: string, detalle?: string) =>
    useAvisos.getState().mostrar("error", mensaje, detalle),
  info: (mensaje: string, detalle?: string) =>
    useAvisos.getState().mostrar("info", mensaje, detalle),
};
